import React from 'react'
import {
  Box,
  Button,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  SxProps,
  Theme,
  SvgIconProps,
  CircularProgress,
} from '@mui/material'
import {
  LocationOn,
  Search,
  HealthAndSafety,
  Psychology,
  MyLocation,
} from '@mui/icons-material'

export const SEARCH_RADIUS_OPTIONS = [
  '5 miles',
  '10 miles',
  // '15 miles',
  '20 miles',
  '40 miles',
]

type AutocompleteOption = Record<string, any> | string

interface SearchBarProps {
  location: string | null
  insurance: string | null
  specialization: string | null
  radius?: string | null
  locationOptions?: AutocompleteOption[]
  insuranceOptions?: AutocompleteOption[]
  specializationOptions?: AutocompleteOption[]
  locationLoading?: boolean
  insuranceLoading?: boolean
  specializationLoading?: boolean
  onLocationChange: (value: string | null) => void
  onInsuranceChange: (value: string | null) => void
  onSpecializationChange: (value: string | null) => void
  onRadiusChange?: (value: string | null) => void
  onLocationSelect?: (option: AutocompleteOption | null) => void
  onInsuranceSelect?: (option: AutocompleteOption | null) => void
  onSpecializationSelect?: (option: AutocompleteOption | null) => void
  onLocationInputChange?: (value: string) => void
  onInsuranceInputChange?: (value: string) => void
  onSpecializationInputChange?: (value: string) => void
  onSearch?: () => void
  isSearchLoading?: boolean
  showSearchButton?: boolean
  compact?: boolean
  sx?: SxProps<Theme>
  accentColor?: string
  accentHoverColor?: string
  thirdFieldLabel?: string
  thirdFieldPlaceholder?: string
  thirdFieldIcon?: React.ElementType<SvgIconProps>
}

const getOptionLabel = (option: AutocompleteOption, fallback = '') => {
  if (!option) return fallback
  if (typeof option === 'string') return option
  return (
    option.label ??
    option.name ??
    option.payer_name ??
    option.description ??
    option.title ??
    fallback
  )
}

const SearchBar: React.FC<SearchBarProps> = ({
  location,
  insurance,
  specialization,
  radius,
  locationOptions,
  insuranceOptions,
  specializationOptions,
  locationLoading = false,
  insuranceLoading = false,
  specializationLoading = false,
  onLocationChange,
  onInsuranceChange,
  onSpecializationChange,
  onRadiusChange,
  onLocationSelect,
  onInsuranceSelect,
  onSpecializationSelect,
  onLocationInputChange,
  onInsuranceInputChange,
  onSpecializationInputChange,
  onSearch,
  isSearchLoading = false,
  showSearchButton = true,
  compact = false,
  sx,
  accentColor = '#2E7DAF',
  accentHoverColor = '#2563a0',
  thirdFieldLabel = 'Specialization',
  thirdFieldPlaceholder = 'Enter specialization',
  thirdFieldIcon,
}) => {
  const [hoveredField, setHoveredField] = React.useState<
    'location' | 'radius' | 'insurance' | 'specialization' | null
  >(null)

  const ThirdFieldIcon = thirdFieldIcon ?? Psychology
  const locationOptionsToUse: AutocompleteOption[] = locationOptions ?? []
  const insuranceOptionsToUse: AutocompleteOption[] = insuranceOptions ?? []
  const specializationOptionsToUse: AutocompleteOption[] =
    specializationOptions ?? []

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: compact ? 2 : 3,
        overflow: 'hidden',
        boxShadow: compact
          ? '0 2px 8px rgba(0,0,0,0.1)'
          : '0 8px 32px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        height: compact ? '60px' : 'auto',
        '&:hover': {
          boxShadow: compact
            ? '0 4px 12px rgba(0,0,0,0.15)'
            : '0 12px 40px rgba(0,0,0,0.15)',
          transform: compact ? 'none' : 'translateY(-2px)',
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexWrap: compact ? 'nowrap' : 'wrap',
          height: compact ? '100%' : 'auto',
          position: 'relative',
        }}
      >
        {/* Location Input */}
        <Box
          onMouseEnter={() => setHoveredField('location')}
          onMouseLeave={() => setHoveredField(null)}
          sx={{
            flexGrow: {
              xs: 1,
              sm: 1,
              md: hoveredField === 'location' ? 2 : hoveredField ? 0.8 : 1,
            },
            flexShrink: {
              xs: 1,
              sm: 1,
              md: hoveredField === 'location' ? 0 : 1,
            },
            flexBasis: {
              xs: '100%',
              sm: '33%',
              md: hoveredField === 'location' ? 'auto' : '0%',
            },
            minWidth: {
              md: hoveredField === 'location' ? '100px' : '0',
            },
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            transition:
              'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            zIndex: hoveredField === 'location' ? 1 : 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : { sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: compact ? '100%' : 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                mb: compact ? 0.25 : 0.5,
                display: 'block',
                fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
                lineHeight: compact ? 1 : 'normal',
              }}
            >
              Search Location
            </Typography>
            <Autocomplete
              freeSolo
              options={locationOptionsToUse}
              loading={locationLoading}
              getOptionLabel={option =>
                getOptionLabel(option as AutocompleteOption)
              }
              isOptionEqualToValue={(option, value) =>
                getOptionLabel(option as AutocompleteOption) ===
                (typeof value === 'string'
                  ? value
                  : getOptionLabel(value as AutocompleteOption))
              }
              value={location || ''}
              onChange={(event, newValue) => {
                const nextValue =
                  typeof newValue === 'string'
                    ? newValue
                    : getOptionLabel(newValue as AutocompleteOption, '')
                onLocationChange(nextValue)
                onLocationSelect?.(
                  typeof newValue === 'string'
                    ? { label: newValue }
                    : (newValue as AutocompleteOption)
                )
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  onLocationChange(newInputValue)
                  onLocationInputChange?.(newInputValue)
                }
              }}
              filterOptions={x => x}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="City, State or Zip code"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationOn
                            sx={{
                              color: accentColor,
                              fontSize: compact
                                ? '1rem'
                                : { sm: '1.2rem', md: '1.5rem' },
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                            }}
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {locationLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    disableUnderline: true,
                    sx: {
                      fontSize: compact
                        ? '0.75rem'
                        : { sm: '0.8rem', md: '0.875rem' },
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      py: compact ? 0.5 : 0,
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Search Radius Input */}
        {onRadiusChange && (
          <Box
            onMouseEnter={() => setHoveredField('radius')}
            onMouseLeave={() => setHoveredField(null)}
            sx={{
              flexGrow: {
                xs: 1,
                sm: 1,
                md: hoveredField === 'radius' ? 2 : hoveredField ? 0.8 : 1,
              },
              flexShrink: {
                xs: 1,
                sm: 1,
                md: hoveredField === 'radius' ? 0 : 1,
              },
              flexBasis: {
                xs: '100%',
                sm: '33%',
                md: hoveredField === 'radius' ? 'auto' : '0%',
              },
              minWidth: {
                md: hoveredField === 'radius' ? '100px' : '0',
              },
              borderRight: showSearchButton
                ? { xs: 'none', md: '1px solid #e0e0e0' }
                : 'none',
              borderColor: 'grey.200',
              bgcolor: 'grey.50',
              transition:
                'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              zIndex: hoveredField === 'radius' ? 1 : 0,
              position: 'relative',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            <Box
              sx={{
                p: compact ? 1 : { sm: 1.5, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: compact ? '100%' : 'auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.500',
                  mb: compact ? 0.25 : 0.5,
                  display: 'block',
                  fontSize: compact
                    ? '0.6rem'
                    : { sm: '0.7rem', md: '0.75rem' },
                  lineHeight: compact ? 1 : 'normal',
                }}
              >
                Search Radius
              </Typography>
              <Autocomplete
                options={SEARCH_RADIUS_OPTIONS}
                value={radius}
                onChange={(event, newValue) => onRadiusChange(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Select radius"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <MyLocation
                              sx={{
                                color: accentColor,
                                fontSize: compact
                                  ? '1rem'
                                  : { sm: '1.2rem', md: '1.5rem' },
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                },
                              }}
                            />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      disableUnderline: true,
                      sx: {
                        fontSize: compact
                          ? '0.75rem'
                          : { sm: '0.8rem', md: '0.875rem' },
                        height: compact ? '24px' : 'auto',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        py: compact ? 0.5 : 0,
                        height: compact ? '24px' : 'auto',
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}

        {/* Insurance Input */}
        <Box
          onMouseEnter={() => setHoveredField('insurance')}
          onMouseLeave={() => setHoveredField(null)}
          sx={{
            flexGrow: {
              xs: 1,
              sm: 1,
              md: hoveredField === 'insurance' ? 2 : hoveredField ? 0.8 : 1,
            },
            flexShrink: {
              xs: 1,
              sm: 1,
              md: hoveredField === 'insurance' ? 0 : 1,
            },
            flexBasis: {
              xs: '100%',
              sm: '33%',
              md: hoveredField === 'insurance' ? 'auto' : '0%',
            },
            minWidth: {
              md: hoveredField === 'insurance' ? '100px' : '0',
            },
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            transition:
              'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            zIndex: hoveredField === 'insurance' ? 1 : 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : { sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: compact ? '100%' : 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                mb: compact ? 0.25 : 0.5,
                display: 'block',
                fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
                lineHeight: compact ? 1 : 'normal',
              }}
            >
              Insurance
            </Typography>
            <Autocomplete
              freeSolo
              options={insuranceOptionsToUse}
              loading={insuranceLoading}
              getOptionLabel={option =>
                getOptionLabel(option as AutocompleteOption)
              }
              filterOptions={(options, state) => options}
              isOptionEqualToValue={(option, value) =>
                getOptionLabel(option as AutocompleteOption) ===
                (typeof value === 'string'
                  ? value
                  : getOptionLabel(value as AutocompleteOption))
              }
              value={insurance || ''}
              onChange={(event, newValue) => {
                const nextValue =
                  typeof newValue === 'string'
                    ? newValue
                    : getOptionLabel(newValue as AutocompleteOption, '')
                onInsuranceChange(nextValue)
                onInsuranceSelect?.(
                  typeof newValue === 'string'
                    ? { label: newValue }
                    : (newValue as AutocompleteOption)
                )
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  onInsuranceChange(newInputValue)
                  onInsuranceInputChange?.(newInputValue)
                }
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select Insurance"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <HealthAndSafety
                            sx={{
                              color: accentColor,
                              fontSize: compact
                                ? '1rem'
                                : { sm: '1.2rem', md: '1.5rem' },
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                            }}
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {insuranceLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    disableUnderline: true,
                    sx: {
                      fontSize: compact
                        ? '0.75rem'
                        : { sm: '0.8rem', md: '0.875rem' },
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      py: compact ? 0.5 : 0,
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Specialization Input */}
        <Box
          onMouseEnter={() => setHoveredField('specialization')}
          onMouseLeave={() => setHoveredField(null)}
          sx={{
            flexGrow: {
              xs: 1,
              sm: 1,
              md:
                hoveredField === 'specialization' ? 2 : hoveredField ? 0.8 : 1,
            },
            flexShrink: {
              xs: 1,
              sm: 1,
              md: hoveredField === 'specialization' ? 0 : 1,
            },
            flexBasis: {
              xs: '100%',
              sm: '33%',
              md: hoveredField === 'specialization' ? 'auto' : '0%',
            },
            minWidth: {
              md: hoveredField === 'specialization' ? '100px' : '0',
            },
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            transition:
              'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            zIndex: hoveredField === 'specialization' ? 1 : 0,
            position: 'relative',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : { sm: 1.5, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: compact ? '100%' : 'auto',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                mb: compact ? 0.25 : 0.5,
                display: 'block',
                fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
                lineHeight: compact ? 1 : 'normal',
              }}
            >
              {thirdFieldLabel}
            </Typography>
            <Autocomplete
              freeSolo
              options={specializationOptionsToUse}
              loading={specializationLoading}
              getOptionLabel={option =>
                getOptionLabel(option as AutocompleteOption)
              }
              filterOptions={(options, state) => options}
              isOptionEqualToValue={(option, value) =>
                getOptionLabel(option as AutocompleteOption) ===
                (typeof value === 'string'
                  ? value
                  : getOptionLabel(value as AutocompleteOption))
              }
              value={specialization || ''}
              onChange={(event, newValue) => {
                const nextValue =
                  typeof newValue === 'string'
                    ? newValue
                    : getOptionLabel(newValue as AutocompleteOption, '')
                onSpecializationChange(nextValue)
                onSpecializationSelect?.(
                  typeof newValue === 'string'
                    ? { label: newValue }
                    : (newValue as AutocompleteOption)
                )
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  onSpecializationChange(newInputValue)
                  onSpecializationInputChange?.(newInputValue)
                }
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={thirdFieldPlaceholder}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <ThirdFieldIcon
                            sx={{
                              color: accentColor,
                              fontSize: compact
                                ? '1rem'
                                : { sm: '1.2rem', md: '1.5rem' },
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                            }}
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {specializationLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    disableUnderline: true,
                    sx: {
                      fontSize: compact
                        ? '0.75rem'
                        : { sm: '0.8rem', md: '0.875rem' },
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      py: compact ? 0.5 : 0,
                      height: compact ? '24px' : 'auto',
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Search Button */}
        {showSearchButton && onSearch && (
          <Button
            variant="contained"
            onClick={onSearch}
            disabled={isSearchLoading}
            sx={{
              bgcolor: accentColor,
              borderRadius: 0,
              minWidth: compact
                ? '50px'
                : { xs: '100%', md: '60px', lg: '80px' },
              flex: { xs: '1 1 100%', md: '0 0 auto' },
              height: compact ? '100%' : 'auto',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: accentHoverColor,
                transform: compact ? 'none' : 'scale(1.05)',
                boxShadow: `0 4px 12px ${accentColor}4D`,
              },
            }}
          >
            {isSearchLoading ? (
              <CircularProgress
                size={compact ? 20 : 28}
                sx={{ color: 'white' }}
              />
            ) : (
              <Search
                sx={{
                  color: 'white',
                  fontSize: compact ? '1.2rem' : { sm: '1.5rem', md: '2rem' },
                }}
              />
            )}
          </Button>
        )}
      </Box>
    </Paper>
  )
}

export default SearchBar
